import { IForm } from 'core-components/classes/models/IForm'
import { IUser } from 'core-components/sitebuilder/models/IUser'
import { IUserRuleExceptions } from 'core-components/sitebuilder/models/IUserRuleExceptions'
import { Logic, Operator } from 'core-components/types'
import { getAppId } from 'src/core/classes/helpers/getAppId'
import { ICleverApp } from 'src/core/classes/models/ICleverApp'
import { ICountryHierarchyInformation } from 'src/core/classes/models/ICountryHierarchyInformation'
import { IDropdownLOV } from 'src/core/classes/models/IDropdownLOV'
import { IErrorLogs } from 'src/core/classes/models/IErrorLogs'
import { userApi } from 'src/core/classes/services/api/UserApi'
import { getLOVFetcher } from 'src/core/classes/services/utils/getLOVFetcher'
import { CORE_FORM } from 'src/core/constants'
import { initCoreNotificationTemplates } from 'src/core/notificationTemplates/initCoreNotificationTemplates'
import { Crud } from 'src/core/types/Crud'
import { CrudInnerStorageFetcher } from 'src/core/types/CrudInnerStorageFetcher'
import { CrudStore } from 'src/core/types/crudStore'
import { ProcedureInnerStorageFetcher } from 'src/core/types/ProcedureInnerStorageFetcher'

export enum CrudStoreItemCore {
  Dropdowns = 'Dropdowns',
  Countries = 'Countries',
  Users = 'Users',
  UsersInfo = 'UsersInfo',
  UsersFromView = 'UsersFromView',
  MSAEmails = 'MSAEmails',
  UserRuleExceptions = 'UserRuleExceptions',
  CleverApp = 'CleverApp',
  AssistantIntegration = 'AssistantIntegration',
  ErrorLogs = 'ErrorLogs',
}

// Deferred runtime read: `=== 'true'` on the baked `__VITE_PROJECT_UPDATING__`
// placeholder is constant-folded at build time, killing the entrypoint substitution.
// `.includes()` is not folded, so the substituted value is evaluated at runtime.
export const applicationUpdating = ['true', true].includes(import.meta.env.VITE_PROJECT_UPDATING)

class InitializationService {
  public init = () => {
    //TODO: Move to another start point
    initCoreNotificationTemplates()

    CrudStore.add(
      new Crud({
        fetcher: new ProcedureInnerStorageFetcher<IUser>('email', {
          readFromTo: async (filter, sort, paging) => {
            return userApi.fetchUsers(filter, paging, sort)
          },
        }),
        hierarchyRoot: null,
        keyField: 'email',
        skipPagingTransform: true,
        defaultPaging: {
          page: 0,
          pageSize: 20,
        },
      }),
      CrudStoreItemCore.Users
    )

    CrudStore.add(
      new Crud({
        fetcher: new CrudInnerStorageFetcher<IUserRuleExceptions>('EntryId', {
          formId: CORE_FORM.userRuleExceptions.id,
          code: CORE_FORM.userRuleExceptions.code,
        }),
        hierarchyRoot: null,
        keyField: 'EntryId',
      }),
      CrudStoreItemCore.UserRuleExceptions
    )

    CrudStore.add(
      new Crud({
        fetcher: new CrudInnerStorageFetcher<IForm>('Id', {
          formId: CORE_FORM.appUsersApps.id,
          code: CORE_FORM.appUsersApps.code,
        }),
        hierarchyRoot: null,
        keyField: 'Id',
        skipPagingTransform: true,
        defaultFilter: {
          logic: Logic.AND,
          filters: [
            {
              field: 'AppId',
              operator: Operator.EQUAL,
              value: getAppId(),
            },
          ],
        },
        defaultPaging: {
          page: 0,
          pageSize: 5,
        },
      }),
      CrudStoreItemCore.UsersFromView
    )

    CrudStore.add(
      new Crud<IDropdownLOV>({
        fetcher: getLOVFetcher(),
        hierarchyField: 'DropdownKey',
        hierarchyRoot: null,
        defaultPaging: {
          page: 0,
          pageSize: 3000,
        },
        defaultSort: {
          field: 'SortOrder',
          dir: 'asc',
        },
        defaultFilter: {
          logic: Logic.AND,
          filters: [
            {
              field: 'AppId',
              operator: Operator.EQUAL,
              value: getAppId(),
            },
          ],
        },
        keyField: 'value',
      }),
      CrudStoreItemCore.Dropdowns
    )

    CrudStore.add(
      new Crud({
        fetcher: new CrudInnerStorageFetcher<ICountryHierarchyInformation>('EntryId', {
          formId: CORE_FORM.countriesList.id,
          code: CORE_FORM.countriesList.code,
        }),
        defaultPaging: {
          page: 0,
          pageSize: 500,
        },
        keyField: 'CC',
      }),
      CrudStoreItemCore.Countries
    )

    CrudStore.add(
      new Crud({
        fetcher: new CrudInnerStorageFetcher<IForm>('EntryId', {
          formId: 24,
          code: 'MSAEmails',
        }),
        defaultPaging: {
          page: 0,
          pageSize: 100,
        },
        keyField: 'EntryId',
      }),
      CrudStoreItemCore.MSAEmails
    )

    CrudStore.add(
      new Crud<IForm>({
        fetcher: new CrudInnerStorageFetcher<IForm>('EntryId', {
          formId: CORE_FORM.orgChart.id,
          code: CORE_FORM.orgChart.code,
        }),
        keyField: 'EntryId',
      }),
      CrudStoreItemCore.UsersInfo
    )

    CrudStore.add(
      new Crud<IForm>({
        fetcher: new CrudInnerStorageFetcher<IForm>('Key', {
          formId: CORE_FORM.assistantIntegration.id,
          code: CORE_FORM.assistantIntegration.code,
        }),
        keyField: 'Key',
      }),
      CrudStoreItemCore.AssistantIntegration
    )

    CrudStore.add(
      new Crud({
        fetcher: new CrudInnerStorageFetcher<ICleverApp[]>('EntryId', {
          formId: CORE_FORM.cleverApps.id,
          code: CORE_FORM.cleverApps.code,
        }),
        keyField: 'EntryId',
        defaultPaging: {
          page: 0,
          pageSize: 10,
        },
      }),
      CrudStoreItemCore.CleverApp
    )

    CrudStore.add(
      new Crud({
        fetcher: new CrudInnerStorageFetcher<IErrorLogs[]>('UID', {
          formId: CORE_FORM.errorLogs.id,
          code: CORE_FORM.errorLogs.code,
        }),
        keyField: 'UID',
        defaultPaging: {
          page: 0,
          pageSize: 100,
        },
      }),
      CrudStoreItemCore.ErrorLogs
    )
  }
}

export default new InitializationService()
